<template>
    <div id="detail-container" class="tw-bg-white tw-w-full tw-h-full tw-flex tw-flex-col">
        <div id="detail-hero" class="tw-shadow-lg tw-z-10 relative tw-flex">
            <div v-if="thread == null && !newParticipant" class="tw-flex tw-items-center">
                <div class="avatar-skeleton md:tw-border-2 md:tw-border-white tw-rounded-full md:tw-mr-4 md:tw-top-3 tw-relative tw-cursor-pointer tw-bg-gray-100"></div>
                <div class="name-skeleton tw-bg-gray-100 tw-w-32 tw-h-6 tw-rounded"></div>
            </div>
            <div v-else class="tw-flex tw-items-center tw-relative">
                <div class="md:tw-hidden tw-mr-4" @click="backToListView">
                    <i class="far fa-chevron-left"></i>
                </div>
                <a :href="'/profile/' + participants[0].username" id="avatar" class="md:tw-border-2 md:tw-border-white tw-rounded-full md:tw-absolute md:tw-top-3 tw-relative tw-cursor-pointer">
                    <thread-avatar
                        v-if="thread || newParticipant"
                        :participants="participants"
                        :participantsInChat="participantsInChat"
                        :thread="thread"
                        :showIndicator="true"
                        :size="80"
                        :shadow="true"
                        :border="true"
                        :variant="'avatar-detail'"
                    ></thread-avatar>
                </a>
                <a :href="'/profile/' + participants[0].username" class="name-wrapper tw-text-lg tw-flex tw-flex-col tw-justify-center tw-cursor-pointer" :class="{'typing':isTyping}">
                    <thread-name
                        v-if="thread || newParticipant"
                        :participants="participants"
                        :size="'xl'"
                    ></thread-name>
                    <span class="typing-indicator tw-font-semibold tw-text-sm tw-text-gray-300">{{translations['messages.is_typing']}}...</span>
                </a>
            </div>
        </div>
        <div class="detail-wrapper" @scroll="getNextMessages" ref="detail" >
            <template v-if="groupedMessages.length > 0  || newParticipant">
                <div id="detail" class="preload tw-px-4 tw-pt-3 tw-pb-6 tw-flex tw-flex-col tw-flex-col-reverse">
                    <div v-for="dates in groupedMessages" :key="dates.id" class=" tw-flex tw-flex-col tw-flex-col-reverse">
                        <thread-message  v-for="message in dates" :key="message.id"
                            :message="message"
                            v-on="$listeners"
                            :caret="showMessageCaret(message, dates)"
                            :ref="'message' + message.id"
                            :thread="thread"
                            :participantsInChat="participantsInChat"
                            :new="message.new"
                        ></thread-message>
                        <div class="date-head tw-rounded-full tw-bg-white tw-px-4 tw-py-3 tw-shadow-md tw-text-center tw-my-3 tw-mx-auto">{{ getSentTime(dates[0].created_at) }}</div>
                    </div>
                    <div v-if="loadingNewMessages" class="tw-py-4 tw-px-4 tw-bg-white tw-font-3xl tw-text-center" >
                        <i class="fa fa-spinner fa-spin tw-text-2xl tw-text-gray-300"></i>
                    </div>
                </div>
            </template>
            <template v-else-if="loadingNewMessages">
                <div class="tw-w-full tw-h-full tw-flex tw-justify-center tw-items-center">
                    <i class="fa fa-spinner fa-spin tw-text-5xl tw-text-gray-300"></i>
                </div>
            </template>
        </div>
        <div id="thread-input" class="tw-mt-auto" ref="threadInput">
            <thread-input :newParticipant="newParticipant" :setActiveThread="setActiveThread" :activeThread="activeThread" v-on="$listeners"></thread-input>
        </div>
    </div>
</template>

<script>
import ThreadAvatar from './ThreadAvatar';
import ThreadName from './ThreadName';
import ThreadMessage from './ThreadMessage';
import ThreadInput from './ThreadInput';
import NotificationCentre from '../../mixins/NotificationCentre';
import Global from '../../mixins/Global.js';

const debounce = require('lodash.debounce');

export default {
	name: 'ThreadDetail',
	props: ['activeThread', 'newParticipantId', 'isTyping', 'not-found', 'setActiveThread', 'fakeThreadActive', 'loadingNewThreadScreen'],
	mixins: [Global, NotificationCentre],
	data() {
		return {
			newParticipant: null,
			curPage: 1,
			loadingNewMessages: false,
			loadingChat: false,
			lastPage: null,
			messagedFetched: true,
		};
	},
	components: {
		'thread-avatar': ThreadAvatar,
		'thread-name': ThreadName,
		'thread-message': ThreadMessage,
		'thread-input': ThreadInput,
	},
	watch: {
        loadingNewThreadScreen(newValue, oldValue) {
            if(newValue !== oldValue) {
                this.loadingNewMessages = this.loadingNewThreadScreen;
            }
        },
		activeThread: async function (newValue, oldValue) {
            // when active thread changes, delete the fake messages in the previous thread
            if(oldValue) {
                // delete fake messages

                const messages = await Message.query().where('fake', true).get();

                messages.forEach(async (message) => {
                    Message.delete(message.id);
                });
            }

			if (oldValue !== newValue && newValue !== null) {
				this.curPage = 1;
				this.fetch(true);
				this.newParticipant = null;
				window.Echo.leave('chat.' + oldValue);
			}
		},
		newParticipantId: function (oldValue, newValue) {
			if (oldValue !== newValue) {
				this.getParticipant(this.newParticipantId);
			}
		},
	},
	computed: {
		thread() {
			return Thread.find(this.activeThread);
		},
		participants() {
			return this.newParticipant
				? [this.newParticipant]
				: this.thread !== null &&
                      this.thread.partners.filter((partner) => partner.id !== user.id);
		},
		messages() {
			return Message.query()
				.where('thread_id', this.activeThread)
				.orderBy('created_at', 'desc')
				.get();
		},
		groupedMessages() {
			let uniq_dates = Array.from(new Set(
				this.messages.sort(function(a, b) {
                        return a.created_at - b.created_at;
                }).map(d => {
					const date = new Date(d.created_at.replace(/-/g, "/"));
					return (date.getMonth() + 1) + '-' + date.getDate() + '-' + date.getFullYear();
				})
			));
			
			const dataMap = [];
			let date_filtered;

			uniq_dates.filter((data) => {
				let key = data;
				date_filtered = this.messages.filter((item) => {
					const d = new Date(item.created_at.replace(/-/g, "/"));
					const date_created =  (d.getMonth() + 1) + '-' + d.getDate() + '-' + d.getFullYear();
					if(key === date_created){
						return item;
					}
				});
				dataMap.push(date_filtered);
			});
			return dataMap;
		},
		participantsInChat() {
			if (this.thread) {
				const partInChat =  this.thread.presence.filter(
					(user) => user.id !== this.$user.id
				);
				return partInChat;
			}
			return [];
		},
	},
	methods: {
		backToListView() {
			this.$emit('closeListView');
		},
		getSentTime(date) {
			moment.locale(this.settings['locale']);

			if(moment(new Date(date)).isSame(new Date(), 'day')) {
				return this.translations['general.today'];
			}
			if(moment(new Date(date)).isSame(moment().subtract(1, 'days'), 'day')) {
				return this.translations['general.yesterday'];
			}
			return moment(date).format('LL');

		},

		fetch: debounce(function(initialFetch = false) {
			this.messagedFetched = false;
			this.loadingNewMessages = true;

			const fetchPage = initialFetch ? this.curPage : this.curPage + 1;

			if (this.activeThread && !this.fakeThreadActive) {
				Message.api()
					.fetchByThreadId(this.activeThread, fetchPage)
					.then((response) => {
						this.lastPage = response.response.data.meta.pagination.last_page;
						this.curPage = response.response.data.meta.pagination.current_page;
						this.loadingNewMessages = false;
						this.messagedFetched = true;
						this.displayAllMessages();
					}).catch(() => {
						this.$emit('not-found');
						this.loadingNewMessages = false;
					});
			} else {
				this.messagedFetched = true;
				this.loadingNewMessages = false;
			}
		}, 250),

		displayAllMessages() {

			// if the last fetched message of this page is in viewport
			// we can display more previous messages, so fetch next page
			if (this.curPage >= this.lastPage) return;
			function isInViewport(el) {
				const rect = el.getBoundingClientRect();
				return (
					rect.top >= 0 &&
                    rect.left >= 0 &&
                    rect.bottom <=
                        (window.innerHeight ||
                            document.documentElement.clientHeight) &&
                    rect.right <=
                        (window.innerWidth ||
                            document.documentElement.clientWidth)
				);
			}

			const lastMessage = this.messages.length > 0 ? this.$refs['message' + this.messages[this.messages.length - 1].id][0].$el : null;

			if (lastMessage && isInViewport(lastMessage)) {
				this.fetch();
			}
		},
		getNextMessages() {
			if (this.curPage >= this.lastPage) return;

			if (this.messagedFetched) {
				const div = this.$refs.detail;

				// *0.99 to load just before user reaches the end of the list
				const topOfWindow = (Math.abs(div.scrollTop) + div.clientHeight) >= (div.scrollHeight * 0.995);

				if (topOfWindow) {
					// this.loadingNewMessages = true;
					this.fetch();
				}
			}
		},
		getParticipant(id) {
			if (this.newParticipantId) {
				const url = `/api/v3/users/${id}`;
				fetch(url)
					.then((response) => response.json())
					.then((data) => {
						this.newParticipant = data.data;
					});
			}
		},
		showMessageCaret(findMessage, messageArray) {
			if (messageArray.length > 0) {
				const messageIndexFn = (message) =>
					message.id === findMessage.id;
				const messageIndex = messageArray.findIndex(messageIndexFn);
				const nextMessageIndex = messageIndex + 1;
				const prevMessageIndex = messageIndex - 1;

				// check if last message is theres
				if (!findMessage.mine) {

					//when there is no next message, always show the caret
					if (nextMessageIndex === messageArray.length) {
						return true;
					}

					// else if next message is not theres, show caret
					if (messageArray[nextMessageIndex].mine !== messageArray[messageIndex].mine) {
						return true;
					}
				}

				if (findMessage.mine) {
					// if it's the first message, show caret
					if (prevMessageIndex < 0) {
						return true;
					}

					// if the previous message is not mine, show caret
					if (messageArray[prevMessageIndex].mine !== messageArray[messageIndex].mine) {
						return true;
					}
				}
				// else, don't show caret
				return false;
			}
		},
	},
	mounted() {
		this.curPage = 1;
		if (this.newParticipantId) {
			this.getParticipant(this.newParticipantId);
		} else {
			if(this.fakeThreadActive) {
				Message.query().where('fake_thread_id', this.activeThread).get();
			} else {
				this.fetch(true);
			}
		}
	},
};
</script>

<style lang="scss">

        .avatar-skeleton {
                width:38px !important;
                height: 38px !important;

            @screen md {
                width:80px !important;
                height: 80px !important;
            }
        }

   .detail-wrapper {
        overflow-y: scroll;
        position: relative;
        display: flex;
        flex-direction: column-reverse;
        height:100%;
        padding-top: 10px;

        #detail {
            bottom: 0;
            display: flex;
            flex-direction: column-reverse;
            height: auto;
            position: relative;

                .thread-message {
                    &:not(.mine) {
                        &.new {
                            animation: scale-in-tl 0.3s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
                        }
                    }
                    &.mine {
                        &.new {
                            animation: scale-in-br 0.3s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
                        }
                    }
                }


            .date-head {
                position: sticky;
                top: -3px;

                &:first-child {
                    background:red;
                }
            }
        }
    }

    #detail-container{
        height: calc(100vh - 190px);
		
		@screen md {
        	height: calc(100vh - 120px);
		}

        @media screen and (min-width:1100px) {
            @apply tw-h-full;
        }
    }

    #detail-hero {
        box-shadow: 0px 3px 15px 0px #0000000d;
        height: 48px;
        padding: 0px 0px 0px 26px;

        @media (min-width:768px) {
            height: 107px;
            // padding: 14px 32px 0px 26px;
        }
    }

    @keyframes scale-in-tl {
        0% {
            transform: scale(0);
            transform-origin: 0% 0%;
            opacity: 0;
        }
        100% {
            transform: scale(1);
            transform-origin: 0% 0%;
            opacity: 1;
        }
    }
        @keyframes scale-in-br {
        0% {
            transform: scale(0);
            transform-origin: 100% 100%;
            opacity: 0;
        }
        100% {
            transform: scale(1);
            transform-origin: 100% 100%;
            opacity: 1;
        }
    }




    .name-wrapper {
        margin-left:8px;

        @media (min-width: 768px) {
            margin-left:105px;
        }



        .typing-indicator {
            opacity:0;
            transition: all .4s ease;
        }
        .name {

            span {
                @media (max-width: 767px) {
                    font-size:1rem !important;
                }
            }
            transform: translateY(10px);
            transition: all .4s ease;
        }

        &.typing {
            .typing-indicator {
                opacity:1;
            }
            .name {
                transform: translateY(0px);
            }
        }
    }
</style>
